import React, { useMemo, useState } from "react";
import { Accordion, Card, useAccordionToggle } from "react-bootstrap";
import { useReportContext } from "../ReportDataContext";
import SchoolProgramFilters, {
  useSchoolProgramFilters,
} from "../SchoolProgramFilters";
import {
  buildSchoolProgramSummary,
  downloadSchoolProgramSummarySheet,
} from "./schoolProgramSummaryExcel";

// Accordion header for one school: shows the school name and that school's
// student total, with a +/- icon reflecting the open state. The first school
// (index 0) starts open to mirror the Accordion's defaultActiveKey.
function SchoolToggle({ eventKey, school, initialOpen }) {
  const [open, setOpen] = useState(initialOpen);
  const headerOnClick = useAccordionToggle(eventKey, () => setOpen(!open));

  return (
    <Card.Header
      onClick={headerOnClick}
      className="d-flex align-items-center"
      style={{ cursor: "pointer" }}
    >
      <span className="font-weight-bold">
        <i
          className={`fa mr-2 ${
            open ? "fa-minus text-muted" : "fa-plus text-success"
          }`}
        />
        {school.school}
      </span>
      <span className="text-muted ml-5">
        Total: <b>{school.total}</b>
      </span>
    </Card.Header>
  );
}

// Streams table for one scope (a school, or the grand total). `footer` is the
// bold subtotal row.
function SummaryTable({ rows, footer, footerLabel, firstColLabel = "Stream" }) {
  return (
    <div className="table-responsive">
      <table className="table table-sm table-bordered mb-0">
        <thead>
          <tr className="text-center">
            <th className="text-left">{firstColLabel}</th>
            <th>Day</th>
            <th>Time</th>
            <th>Duration</th>
            <th>Students</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.code || row.abbr} className="text-center">
              <td className="text-left">{row.abbr}</td>
              <td>{row.day || ""}</td>
              <td>{row.time || ""}</td>
              <td>{row.duration || ""}</td>
              <td>{row.count}</td>
            </tr>
          ))}
          <tr className="text-center font-weight-bold bg-light">
            <td className="text-left">{footerLabel}</td>
            <td></td>
            <td></td>
            <td></td>
            <td>{footer.total}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function SchoolProgramSummaryReportView({ reports }) {
  const branch = useReportContext().AUTH.branch;

  // Same filters as the School Program Registered Student List report; the
  // registered-list endpoint returns the program name as `program_type`.
  const { filteredReports, filters } = useSchoolProgramFilters(reports, branch, {
    programField: "program_type",
  });

  const { schools, grand } = useMemo(
    () => buildSchoolProgramSummary(filteredReports),
    [filteredReports]
  );

  return (
    <div>
      <SchoolProgramFilters filters={filters} />

      <div className="row mb-3">
        <div className="col-12 text-right">
          <button
            type="button"
            className="btn btn-primary"
            disabled={!filteredReports.length}
            onClick={() =>
              downloadSchoolProgramSummarySheet(
                filteredReports,
                branch.branch_name
              )
            }
          >
            Download Summary
          </button>
        </div>
      </div>

      {schools.length > 0 ? (
        <>
          <Accordion defaultActiveKey="0">
            {schools.map((school, i) => (
              <Card key={school.school}>
                <SchoolToggle
                  eventKey={String(i)}
                  school={school}
                  initialOpen={i === 0}
                />
                <Accordion.Collapse eventKey={String(i)}>
                  <Card.Body>
                    <SummaryTable
                      rows={school.streams}
                      footer={school}
                      footerLabel="School Total"
                    />
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            ))}
          </Accordion>

          <Card className="mt-3">
            <Card.Body>
              <SummaryTable
                rows={[]}
                footer={grand}
                footerLabel="Grand Total"
                firstColLabel=""
              />
            </Card.Body>
          </Card>
        </>
      ) : (
        <div className="text-muted mt-3">
          No results match the selected filters.
        </div>
      )}
    </div>
  );
}
